import React from "react";
import { Sticky } from "../../../BuildingBlocks/Sticky";
import { CRMSingleViewHeader } from "../../../Simple/CRMSingleViewHeader/CRMSingleViewHeader";
import {
    TCaseTitleCheckForm,
    TCaseTitleCheckFieldGroup,
    caseTitleCheckFieldHelp,
    getCaseTitleCheckFieldGroupCounts,
    getCaseTitleCheckFieldsToHideForGroup,
    getCompletedStateTupleFromData,
} from "../../../../../../domain/codecs/form/CaseTitleCheckForm";
import { CRMCaseTitleCheckIdentifyingSummary } from "../CRMCaseTitleCheckIdentifyingSummary/CRMCaseTitleCheckIdentifyingSummary";
import { CRMCaseTitleCheckStatusPill } from "../CRMCaseTitleCheckStatusPill/CRMCaseTitleCheckStatusPill";
import { Padding } from "../../../BuildingBlocks/Padding";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { BackgroundColour } from "../../../BuildingBlocks/BackgroundColour";
import { CRMColors } from "../../../../models/CRMColors";
import { MinHeight } from "../../../BuildingBlocks/MinHeight";
import { CRMCodecEditForm } from "../../../CRMCodecEditForm/CRMCodecEditForm";
import { TitleCheckEditable } from "../../../../../../domain/codecs/TitleCheck";
import { YesNoUnknown } from "../../../../../../domain/codecs/YesNoUnknown";
import { CRMAutosaveIndicatorWrap } from "../../../CRM/CRMAutosaveIndicatorWrap/CRMAutosaveIndicatorWrap";
import { CRMColumnSpacer } from "../../../CRMColumnSpacer/CRMColumnSpacer";
import { CRMRadioButtonsFormInput } from "../../../CRMFormInputs/CRMRadioButtonsFormInput";
import { CRMInputList } from "../../../Complex/CRMInputList/CRMInputList";
import CRMInputLabelAndErrorWrapComponent from "../../../CRMInputLabelAndErrorWrapComponent/CRMInputLabelAndErrorWrapComponent";
import CRMAccordion from "../../../CRM/CRMAccordionComponent/CRMAccordion";
import { CRMTitleSubSection } from "../../../CRMTitleSubSection/CRMTitleSubSection";
import { CRMLink } from "../../../CRMLink/CRMLink";
import { CRMParagraph } from "../../../Simple/CRMParagraph/CRMParagraph";
import { WeightMedium } from "../../../WeightMedium/WeightMedium";
import { CRMCasePropertyReferredDocumentsTable } from "../../../CRMCasePropertyReferredDocumentsTable/CRMCasePropertyReferredDocumentsTable";

export const CRMCaseTitleCheckSingleView = (props: {
    shortAddress: string,
    form: TCaseTitleCheckForm,
    onClose: () => void,
    onChange: (form: TCaseTitleCheckForm) => void;
    onAddTitleCheckIssue: (form: TCaseTitleCheckForm, description: string) => void;
    onDeleteTitleCheckIssue: (form: TCaseTitleCheckForm, id: string) => void;
}): JSX.Element => {
    const editedAndReadOnly = {
        ...props.form.edited,
        ...props.form.children.read_only,
    };
    const titleCheckFormState = getCompletedStateTupleFromData(editedAndReadOnly);
    const counts = getCaseTitleCheckFieldGroupCounts(editedAndReadOnly);

    const overrideComps = {
        has_other_title_issues: (overrideProps) =>
            <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                <CRMRadioButtonsFormInput
                    {...overrideProps}
                    onChange={overrideProps.onChange as (string) => void}
                    codec={YesNoUnknown}
                />
                {overrideProps.model === "yes" &&
                    <CRMInputLabelAndErrorWrapComponent label={"Other Title Issues"} guidance="Please add any other issues that are not already part of the checklist (note: charges & restrictions are stored in the Charges & Restrictions block)">
                        <CRMInputList
                            value={props.form.children.title_check_issues.map((tci) => ({
                                meta: tci,
                                displayString: tci.description,
                            }))}
                            onAdd={(description) => props.onAddTitleCheckIssue(props.form, description)}
                            onDelete={(i) => props.onDeleteTitleCheckIssue(props.form, i.meta.id)}
                            disabled={props.form.edited.all_other_title_issues_added !== null}
                            mode="always-open"
                            disableAdd={props.form.children.new_title_check_issue_form.status === "submitting"}
                            disableDelete={props.form.children.delete_title_check_issue_form.status === "submitting"}
                        />
                    </CRMInputLabelAndErrorWrapComponent>
                }
            </SpacingColumn>
    };

    const renderForm = (group: TCaseTitleCheckFieldGroup): JSX.Element =>
        <CRMCodecEditForm
            codec={TitleCheckEditable}
            model={props.form.edited}
            validationErrors={props.form.validationErrors}
            onChange={(p) =>
                props.onChange({
                    ...props.form,
                    edited: {
                        ...props.form.edited,
                        ...p,
                    },
                })
            }
            hideFields={getCaseTitleCheckFieldsToHideForGroup(editedAndReadOnly, group)}
            overrideComps={group === "manual" ? overrideComps : {}}
            fieldHelp={caseTitleCheckFieldHelp}
        />;

    const renderSectionHeading = (text: string): JSX.Element =>
        <CRMTitleSubSection>{text}</CRMTitleSubSection>;

    const hasReferredDocuments =
        props.form.children.read_only.is_registered === true
        && props.form.children.read_only.title_number.trim() !== "";

    return <>
        {/* HEADER */}
        <Sticky zIndex={4}>
            <CRMSingleViewHeader
                title={props.shortAddress}
                onClick={props.onClose}
            >
                <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
                    <Padding spacing={`0px ${CRMSpacing.MEDIUM}`}>
                        <CRMCaseTitleCheckStatusPill
                            formState={titleCheckFormState}
                        />
                    </Padding>
                </BackgroundColour>
            </CRMSingleViewHeader>
        </Sticky>

        <BackgroundColour colour={CRMColors.NEUTRAL_PAPER}>
            <MinHeight height="calc(100vh - 216px)">
                <Padding spacing={CRMSpacing.MEDIUM}>
                    <SpacingColumn spacing={CRMSpacing.LARGE}>
                        <SpacingColumn spacing={CRMSpacing.SMALL}>
                            <CRMCaseTitleCheckIdentifyingSummary
                                tenure={props.form.children.read_only.tenure}
                                isRegistered={props.form.children.read_only.is_registered}
                                titleNumber={props.form.children.read_only.title_number}
                            />

                            {props.form.children.title_documents.length > 0 &&
                                <CRMInputLabelAndErrorWrapComponent label="Title and deeds">
                                    <SpacingColumn spacing={CRMSpacing.TINY}>
                                        {props.form.children.title_documents.map((document) =>
                                            <CRMParagraph key={document.case_document_file_id}>
                                                <CRMLink
                                                    href={`${env.REACT_APP_API_URL}/web/cases/${document.case_id}/documents/${document.case_document_id}/files/${document.case_document_file_id}/download`}
                                                    target="_blank"
                                                    linkStyle="normal"
                                                >
                                                    <WeightMedium>
                                                        {document.name}.{document.file_extension}
                                                    </WeightMedium>
                                                </CRMLink>
                                            </CRMParagraph>
                                        )}
                                    </SpacingColumn>
                                </CRMInputLabelAndErrorWrapComponent>
                            }
                        </SpacingColumn>

                        {hasReferredDocuments &&
                            <CRMCasePropertyReferredDocumentsTable
                                casePropertyId={props.form.edited.id}
                            />
                        }

                        <CRMAutosaveIndicatorWrap status={props.form.status}>
                            <CRMColumnSpacer size="large"/>

                            <SpacingColumn spacing={CRMSpacing.LARGE}>
                                {counts.manual > 0 && <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                                    {renderSectionHeading(`Manual checks (${counts.manual})`)}
                                    {renderForm("manual")}
                                </SpacingColumn>}

                                {counts.flagged > 0 && <SpacingColumn spacing={CRMSpacing.MEDIUM}>
                                    {renderSectionHeading(`Flagged from register (${counts.flagged})`)}
                                    {renderForm("flagged")}
                                </SpacingColumn>}

                                {counts.clean > 0 && <CRMAccordion
                                    title={`Auto-checks passed (${counts.clean})`}
                                    defaultsOpen={false}
                                >
                                    {renderForm("clean")}
                                </CRMAccordion>}
                            </SpacingColumn>
                        </CRMAutosaveIndicatorWrap>
                    </SpacingColumn>
                </Padding>
            </MinHeight>
        </BackgroundColour>
    </>;
};
